import {XIcon} from '@heroicons/react/outline';
import type {ReactElement} from 'react';
import {useCallback, useState} from 'react';

import {Button, PlusIcon, Select} from '@/components';
import type {BusinessLimitTagMap, BusinessLimitTags} from '@/types';
import {BusinessFeeComparator, BusinessLimitField} from '@/types';
import {formatSelectOptionItem} from '@/utils/merchant';
import {showErrorMessage} from '@/utils/showErrorMessage';

import {LimitRuleValueInput} from './LimitRuleValueInput';
type Props = {
  onChange: (value: BusinessLimitTagMap) => void;
  tags?: BusinessLimitTags;
  value?: string;
  dataCy?: string;
  onRemove: (tag: BusinessLimitField) => void;
  fieldsToOmit: BusinessLimitField[];
  lockedFields?: BusinessLimitField[];
};

const mapFieldToComparators = (
  field: BusinessLimitField,
): BusinessFeeComparator[] => {
  switch (field) {
    case BusinessLimitField.TransactionCount:
    case BusinessLimitField.TransactionVolume:
    case BusinessLimitField.TransactionAmount:
      return [
        BusinessFeeComparator.Equals,
        BusinessFeeComparator.LessThan,
        BusinessFeeComparator.GreaterThan,
        BusinessFeeComparator.Between,
      ];

    default:
      return [BusinessFeeComparator.Equals];
  }
};
const mapOptionsToList = (options: string[], fieldsToOmit: string[] = []) =>
  options
    ?.filter((item) => !fieldsToOmit.includes(item))
    ?.map((item) => ({
      key: formatSelectOptionItem(item || ''),
      value: item || '',
    }));

export const LimitPredicateInput = ({
  onChange,
  tags,
  onRemove,
  fieldsToOmit,
  dataCy,
  lockedFields = [],
}: Props): ReactElement => {
  const lockedFieldSet = new Set(lockedFields);
  const predicateFieldOptions = mapOptionsToList(
    Object.values(BusinessLimitField),
    [...fieldsToOmit, ...lockedFields],
  );
  const [selectedField, setSelectedField] = useState<BusinessLimitField>(
    BusinessLimitField.FromCountry,
  );
  const [selectedComparator, setSelectedComparator] =
    useState<BusinessFeeComparator>(BusinessFeeComparator.Equals);

  const [selectedValue, setSelectedValue] = useState<string>('');
  const [filteredComparators, setFilteredComparators] = useState<
    BusinessFeeComparator[]
  >(() => mapFieldToComparators(selectedField));

  const comparatorOptions = mapOptionsToList(filteredComparators);

  const handleFieldChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const field = event.target.value as BusinessLimitField;
    setSelectedField(field);
    const comparators = mapFieldToComparators(field);
    setFilteredComparators(comparators);
    setSelectedComparator(comparators[0]);
  };

  const handleComparatorChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setSelectedComparator(event.target.value as BusinessFeeComparator);
  };

  const handleValueChange = (val: string) => {
    if (val) {
      setSelectedValue(val);
    }
  };

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e?.preventDefault();
      if (!selectedField || !selectedComparator || !selectedValue) {
        showErrorMessage('Please provide all required filter values');
        return;
      }
      const tag = {
        [selectedField]: `${selectedComparator}::${selectedValue}`,
      };

      onChange(tag);
      setSelectedValue('');
    },
    [selectedField, selectedComparator, selectedValue],
  );
  const tagList = Object.keys(tags ?? {});
  return (
    <div>
      <label
        htmlFor="price"
        className="block text-sm font-medium leading-6 text-gray-700 dark:text-gray-200"
      >
        Applies to any transaction with:
      </label>
      <ul className="flex flex-row flex-wrap gap-2 mb-1">
        {(tagList || [])?.length > 0
          ? tagList?.map((tagKey) => {
              const isLocked = lockedFieldSet.has(tagKey as BusinessLimitField);
              return (
                <li
                  key={tagKey}
                  data-cy={dataCy ? `${dataCy}-rule-chip` : undefined}
                  className={`flex items-center gap-2 p-1 px-2 rounded bg-indigo-300 text-sm dark:bg-indigo-900 dark:text-white ${
                    isLocked ? '' : 'dark:hover:bg-indigo-800 cursor-pointer'
                  }`}
                >
                  {`${tagKey} ${tags?.[tagKey as BusinessLimitField]?.replace('::', ' ')}`}
                  {!isLocked && (
                    <XIcon
                      data-cy={
                        dataCy ? `${dataCy}-rule-chip-remove` : undefined
                      }
                      className="w-4 h-4 text-red-500 cursor-pointer"
                      onClick={() => {
                        onRemove(tagKey as BusinessLimitField);
                        return false;
                      }}
                    />
                  )}
                </li>
              );
            })
          : ''}
      </ul>
      <hr className="border-1 border-gray-200 dark:border-dark-borderMid mt-2 mb-4 w-full" />
      <div className="mt-1 rounded-md shadow-sm w-full flex gap-2">
        <Select
          id="field"
          dataCy={dataCy ? `${dataCy}-field` : undefined}
          name="field"
          className="w-full lg:w-fit"
          onChange={handleFieldChange}
          value={selectedField}
          options={predicateFieldOptions}
          placeholder="---Select One---"
        />
        <Select
          dataCy={dataCy ? `${dataCy}-comparator` : undefined}
          id="comparator"
          name="comparator"
          className="w-full lg:w-fit"
          onChange={handleComparatorChange}
          value={selectedComparator}
          options={comparatorOptions}
          placeholder="---Select One---"
        />
      </div>
      <div className="mt-2">
        <div className="flex flex-col gap-2">
          <div className="w-full lg:w-fit">
            <LimitRuleValueInput
              dataCy={dataCy ? `${dataCy}-value` : undefined}
              field={selectedField}
              comparator={selectedComparator}
              handleValueChange={handleValueChange}
            />
          </div>
          <Button
            type="button"
            dataCy={dataCy ? `${dataCy}-add-rule` : undefined}
            variant="solid"
            className="w-fit"
            disabled={!selectedField || !selectedComparator || !selectedValue}
            onClick={(e: React.FormEvent) => handleSubmit(e)}
          >
            Add <PlusIcon className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};
